import './App.css';
import { Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Toaster } from 'react-hot-toast';
import AutoLogout from './utils/Autologout/AutoLogout';
import RequiredAuthRoute from './routes/RequiredAuthRoute';
import Layout from './layout/Layout';
import CompleteCategoryItemSection from './pages/Checklists/CompleteCategoryChecklist/CompleteCategoryItemSection';
import CourseStart from './pages/Courses/CourseStart';
import OrganizationList from './pages/OrganizationList/OrganizationList'
import Loading from './components/Loading/Loading';

const LazyLoad = (componentPath) => lazy(() => import(`${componentPath}`));
const Login = lazy(() => import('./pages/Login/Login'));
const Credentials = LazyLoad('./pages/Credentials/Credentials');
const Account = LazyLoad('./pages/Account/Account');
const AccountEdit = LazyLoad('./pages/AccountEdit');
const Specialties = LazyLoad('./pages/Specialties/Specialties');
const SpecialtiesEdit = LazyLoad('./pages/Specialties/SpecialtiesEdit');
const SpecialtiesCreate = LazyLoad('./pages/Specialties/SpecialtiesCreate');
const WorkExperience = LazyLoad('./pages/WorkExperience/WorkExperience');
const WorkExperienceCreate = LazyLoad('./pages/WorkExperience/WorkExperienceCreate');
const WorkExperienceEdit = LazyLoad('./pages/WorkExperience/WorkExperienceEdit');
const Education = LazyLoad('./pages/Education/Education');
const EducationForm = LazyLoad('./pages/Education/EducationForm');
const Licenses = LazyLoad('./pages/Licenses/Licenses');
const LicensesCreate = LazyLoad('./pages/Licenses/LicensesCreate');
const LicensesEdit = LazyLoad('./pages/Licenses/LicensesEdit');
const Certifications = LazyLoad('./pages/Certifications/Certifications');
const CertificationsCreate = LazyLoad('./pages/Certifications/CertificationsCreate');
const CertificationsEdit = LazyLoad('./pages/Certifications/CertificationsEdit');
const Documents = LazyLoad('./pages/Documents/Documents');
const DocumentCreate = LazyLoad('./pages/Documents/DocumentCreate');
const DocumentsEdit = LazyLoad('./pages/Documents/DocumentsEdit');
const ChecklistContainer = LazyLoad('./pages/Checklists/ChecklistContainer');
const CompleteCategoryItem = LazyLoad('./pages/Checklists/CompleteCategoryChecklist/CompleteCategoryItem');
const CourseContainer = LazyLoad('./pages/Courses/CourseContainer');
const CourseChapter = LazyLoad('./pages/Courses/CourseChapter');
const CourseProgressStart = LazyLoad('./pages/Courses/CourseProgressStart');
const CourseExamCompleted = LazyLoad('./pages/CourseExamCompleted');
const TestContainer = LazyLoad('./pages/Tests/TestContainer');
const TestStartExam = LazyLoad('./pages/TestStartExam');
const TestExamCompleted = LazyLoad('./pages/TestExamCompleted');
const ChecklistExam = LazyLoad('./pages/ChecklistExam');
const ChecklistExamCompleted = LazyLoad('./pages/ChecklistExamCompleted');
const ForgotPassword = LazyLoad('./pages/ForgotPassword/ForgotPassword');
const ResetPassword = LazyLoad('./pages/ResetPassword/ResetPassword');
const MagicLink = LazyLoad('./pages/MagicLink');
const MagicLinkExpired = LazyLoad('./pages/MagicLink/MagicLinkExpired/MagicLinkExpired');
const WorkReferenceRequest = LazyLoad('./pages/WorkReferencesRequest/WorkReferencesRequest');
const WorkReference = LazyLoad('./pages/WorkReference/WorkReference');
const WorkReferenceCreate = LazyLoad('./pages/WorkReferenceCreate/WorkReferenceCreate');
const WorkReferenceComplete = LazyLoad('./pages/WorkReferenceComplete/WorkReferenceComplete');
const WorkReferenceEdit = LazyLoad('./pages/WorkReferenceEdit/WorkReferenceEdit');
const NotFound = LazyLoad('./pages/NotFound/NotFound');

function App() {
  return (
    <div>
      <AutoLogout />
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Layout />}>
            {/* Public Routes */}
            <Route path="/" element={<Login />} />
            <Route path="/magic_auth/:magic_token" element={<MagicLink />} />
            <Route path="/forgot_password" element={<ForgotPassword />} />
            <Route path="/magic_link_expire" element={<MagicLinkExpired />} />
            <Route path="/reset_password" element={<ResetPassword />} />
            <Route path="/reference" element={<WorkReferenceRequest />} />
            <Route path="/reference_complete/:params?" element={<WorkReferenceComplete />} />
            {/* Protected Routes */}
            <Route element={<RequiredAuthRoute />}>
              <Route path="/roles" element={<OrganizationList />} />
              <Route path="/organizations/credentials" element={<Credentials />} />
              <Route path="/organizations/account" element={<Account />} />
              <Route path="/organizations/account/edit" element={<AccountEdit />} />
              {/* UNA Test */}
              <Route path='/tests/:test' element={<TestContainer />} />
              <Route path='/tests/:testId/start' element={<TestStartExam />} />
              <Route path='/tests/:testId/complete' element={<TestExamCompleted />} />
              {/* UNA Checklists */}
              <Route path='/checklists/:checklist' element={<ChecklistContainer />} />
              <Route path='/checklists/:checklist/categories/:category' element={<CompleteCategoryItem />} />
              <Route path='/checklists/:checklist/categories/:category/sections/:section' element={<CompleteCategoryItemSection />} />
              <Route path='/checklists/:checklist/start' element={<ChecklistExam />} />
              <Route path='/checklists/:checklist/complete' element={<ChecklistExamCompleted />} />
              {/* Courses */}
              <Route path='/courses/:course' element={<CourseContainer />} />
              <Route path='/courses/:course/chapters' element={<CourseStart />} />
              <Route path='/courses/:course/start' element={<CourseProgressStart />} />
              <Route path='/courses/:course/chapters/:chapter' element={<CourseChapter />} />
              <Route path='/courses/:courseId/complete' element={<CourseExamCompleted />} />
              {/* Wallet */}
              <Route path="/specialties/:id" element={<Specialties />} />
              <Route path="/specialties/edit/:id" element={<SpecialtiesEdit />} />
              <Route path="/specialties/create" element={<SpecialtiesCreate />} />
              {/* Documents */}
              <Route path="/documents/work_experiences/:id" element={<WorkExperience />} />
              <Route path="/documents/work_experiences/edit/:id" element={<WorkExperienceEdit />} />
              <Route path="/documents/work_experiences/create" element={<WorkExperienceCreate />} />
              <Route path="/documents/education/:id" element={<Education />} />
              <Route path="/documents/education/edit/:id" element={<EducationForm />} />
              <Route path="/documents/education/create" element={<EducationForm />} />
              <Route path="/documents/licenses/:id" element={<Licenses />} />
              <Route path="/documents/licenses/edit/:id" element={<LicensesEdit />} />
              <Route path="/documents/licenses/create" element={<LicensesCreate />} />
              <Route path="/documents/certifications/:id" element={<Certifications />} />
              <Route path="/documents/certifications/edit/:id" element={<CertificationsEdit />} />
              <Route path="/documents/certifications/create" element={<CertificationsCreate />} />
              <Route path="/documents/references/:id" element={<WorkReference />} />
              <Route path="/documents/references/edit/:id" element={<WorkReferenceEdit />} />
              <Route path="/documents/references/create" element={<WorkReferenceCreate />} />
              <Route path="/documents/:docName/create" element={<DocumentCreate />} />
              <Route path="/documents/:docName/:id" element={<Documents />} />
              <Route path="/documents/:docName/edit/:id" element={<DocumentsEdit />} />
              {/* Not Found */}
              <Route path="*" element={<NotFound />} />
              <Route path="/error" element={<NotFound />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
      <Toaster />
    </div>
  );
}

export default App;
